import {
  calculateNominalDilutedConcentration,
  labDnaStepMaxConcentrationWithoutDilutionNgUl,
} from '@resistapp/common/lab-dna-step';
import { isWaterType, type SampleType } from '@resistapp/common/sampling-point-types';
import { NormalisationMode, SampleNormalisationMetadata } from '@resistapp/common/types';
import { isNil } from 'lodash';

export interface CopyNumberCoefficients {
  slope: number;
  intercept: number;
}

export const SUPPRESSED_VOLUME_MODES = [
  NormalisationMode.LITRE_16S,
  NormalisationMode.LITRE_AY,
  NormalisationMode.HOUR,
  NormalisationMode.MG_SS,
  NormalisationMode.MG_BOD,
] as const;

export interface WaterVolumeCompletenessSummary {
  applicableCount: number;
  withVolumeCount: number;
  withoutVolumeCount: number;
  isMixed: boolean;
}

export interface WaterVolumeCompletenessSampleMetadata
  extends Pick<SampleNormalisationMetadata, 'filteredVolume' | 'auxExtractionFilteredVolumeMl'> {
  sampleType: SampleType | null | undefined;
}

export const sixteenSCopyNumberCoefficients: CopyNumberCoefficients = {
  slope: -3.34,
  intercept: 37.695,
};

export function summariseWaterVolumeCompleteness(
  samples: ReadonlyArray<WaterVolumeCompletenessSampleMetadata>,
): WaterVolumeCompletenessSummary {
  const applicable = samples.filter(sample => sample.sampleType != null && isWaterType(sample.sampleType));
  const withVolumeCount = applicable.reduce((count, sample) => {
    const filteredVolume = getEffectiveFilteredVolume(sample);
    const hasFilteredVolume = typeof filteredVolume === 'number' && Number.isFinite(filteredVolume);
    return hasFilteredVolume ? count + 1 : count;
  }, 0);
  const applicableCount = applicable.length;
  const withoutVolumeCount = applicableCount - withVolumeCount;
  return {
    applicableCount,
    withVolumeCount,
    withoutVolumeCount,
    isMixed: withVolumeCount > 0 && withoutVolumeCount > 0,
  };
}

export function wouldIntroduceMixed(
  before: WaterVolumeCompletenessSummary,
  after: WaterVolumeCompletenessSummary,
): boolean {
  return !before.isMixed && after.isMixed;
}

export function calculateNormalizationFactors(sample: SampleNormalisationMetadata) {
  const volumeNormalisationFactor = getVolumeNormalisationFactor(sample);
  const flowNormlisationFactor = getFlowNormlisationFactor(sample, volumeNormalisationFactor);
  const suspendedSolidsNormlisationFactor = getSuspendedSolidsNormlisationFactor(sample, volumeNormalisationFactor);
  const bodNormlisationFactor = getBodNormlisationFactor(sample, volumeNormalisationFactor);

  return {
    volumeNormalisationFactor,
    flowNormlisationFactor,
    suspendedSolidsNormlisationFactor,
    bodNormlisationFactor,
  };
}

export function getVolumeNormalisationFactor(sample: SampleNormalisationMetadata) {
  const elutedDnaConcentration = sample.primElutedDnaConcentration ?? sample.auxQaConcentrationNgUl;
  const elutedDnaVolume =
    sample.primElutedDnaVolume ?? sample.auxExtractionElutionVolumeUl ?? sample.auxQaMeasuredVolumeUl;
  // Primary diluted DNA concentration is left unfilled if it's the same as primary eluted DNA concentration.
  // For auxiliary DNA-step values, only infer the QA concentration as final concentration when no dilution is required.
  const hasNoDilutionRequiredAuxiliaryQaConcentration =
    sample.auxQaConcentrationNgUl != null &&
    sample.auxQaConcentrationNgUl <= labDnaStepMaxConcentrationWithoutDilutionNgUl;
  const auxiliaryNoDilutionDnaConcentration =
    sample.auxQaConcentrationNgUl != null &&
    sample.auxQaConcentrationNgUl > 0 &&
    sample.auxQaConcentrationNgUl <= labDnaStepMaxConcentrationWithoutDilutionNgUl
      ? sample.auxQaConcentrationNgUl
      : undefined;
  // A measured post-dilution spot check wins. Without one (spot checks are optional), the planned concentration from
  // the stored dilution calculation is used, so a skipped spot check neither drops the volume-normalised results nor
  // lets the undiluted eluted concentration stand in for lab-diluted DNA.
  const auxiliaryDilutionDnaConcentration = hasNoDilutionRequiredAuxiliaryQaConcentration
    ? undefined
    : sample.auxDilutionConcentrationNgUl ??
      calculateNominalDilutedConcentration(
        sample.auxQaConcentrationNgUl,
        sample.auxDilutionSampleVolumeUl,
        sample.auxDilutionDesiredFinalVolumeUl,
      ) ??
      undefined;
  const primaryNoDilutionDnaConcentration = sample.primDilutedDnaConcentrationUnavailable
    ? undefined
    : sample.primElutedDnaConcentration;
  const dilutedDnaConcentration =
    sample.primDilutedDnaConcentration ??
    auxiliaryDilutionDnaConcentration ??
    primaryNoDilutionDnaConcentration ??
    auxiliaryNoDilutionDnaConcentration;
  const filteredVolume = getEffectiveFilteredVolume(sample);
  const normalisationFactor =
    isNil(dilutedDnaConcentration) || isNil(elutedDnaConcentration) || isNil(elutedDnaVolume) || isNil(filteredVolume)
      ? undefined
      : ((100 / dilutedDnaConcentration) * elutedDnaConcentration * elutedDnaVolume) / filteredVolume;
  return normalisationFactor;
}

export function getEffectiveFilteredVolume(
  sample: Pick<SampleNormalisationMetadata, 'filteredVolume' | 'auxExtractionFilteredVolumeMl'>,
) {
  return sample.auxExtractionFilteredVolumeMl ?? sample.filteredVolume;
}

export function getFlowNormlisationFactor(
  sample: SampleNormalisationMetadata,
  volumeNormalisationFactor: number | undefined,
) {
  return isNil(volumeNormalisationFactor) || isNil(sample.flowRate)
    ? undefined
    : volumeNormalisationFactor * sample.flowRate; // copies/L * L/h = copies/h
}

export function getSuspendedSolidsNormlisationFactor(
  sample: SampleNormalisationMetadata,
  volumeNormalisationFactor: number | undefined,
) {
  return isNil(volumeNormalisationFactor) || !sample.suspendedSolids
    ? undefined
    : volumeNormalisationFactor / sample.suspendedSolids; // copies/L / mg/L = copies/mg
}

export function getBodNormlisationFactor(
  sample: SampleNormalisationMetadata,
  volumeNormalisationFactor: number | undefined,
) {
  return isNil(volumeNormalisationFactor) || !sample.bod ? undefined : volumeNormalisationFactor / sample.bod; // copies/L / mg/L = copies/mg
}

export function interpolateCopyNumber(
  meanCt: number | null | undefined,
  coefficients: CopyNumberCoefficients | null | undefined = sixteenSCopyNumberCoefficients,
) {
  if (meanCt == null || !coefficients) {
    return undefined;
  }
  const { slope, intercept } = coefficients;
  if (!Number.isFinite(slope) || slope === 0 || !Number.isFinite(intercept)) {
    return undefined;
  }
  const logCopies = (meanCt - intercept) / slope;
  const copies = 10 ** logCopies;
  return Number.isFinite(copies) ? copies : undefined;
}
